/**
 * TEMPORARY right pane of the cash-drawer purchase widget — browse ALL articles
 * (active AND inactive) as a category tree, filter by one/many categories, and
 * BUY an article for the currently-selected user with one click, mirroring the
 * kiosk (strichliste prices it server-side from the article id). Each buy shows
 * a transient Undo (the kiosk's undo = DELETE the transaction, reverting the
 * balance).
 *
 * Buy/undo go through the thin admin pass-throughs
 *   POST   /api/users/{sid}/buy            {article_id}
 *   DELETE /api/users/{sid}/transactions/{tx}
 * (a richer admin/POS buy route is coming on another branch).
 *
 * Remove later together with PurchaseSettleWidget.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  FloatButton,
  Grid,
  Input,
  Modal,
  Select,
  Space,
  Tree,
  Typography,
  notification,
} from "antd";
import { CameraOutlined } from "@ant-design/icons";
import type { DataNode } from "antd/es/tree";

import { ApiError, type ChainRow, api } from "../../api/client";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { CameraScanView } from "../../components/CameraScanView";
import { cameraScanSupported } from "../../utils/cameraScan";

const { Text } = Typography;

// Synthetic category id for chains with no category (null) so they bucket too.
const UNCAT = -1;

const fmtCents = (c: number | null | undefined): string =>
  c === null || c === undefined
    ? "—"
    : new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(c / 100);

// Subtle alternating-row tint for the article rows (zebra striping). Reads as a
// faint highlight on both the light and dark themes.
const ZEBRA_BG = "rgba(22, 119, 255, 0.08)";

export function ArticleBuyPane({
  strichlisteUserId,
  userName,
  onBought,
  onSelectUser,
}: {
  strichlisteUserId: number | null;
  userName: string | null;
  onBought?: (boughtTxId?: number) => void;
  /** Switch the widget's selected user (e.g. jump to a scanned voucher's holder). */
  onSelectUser?: (strichlisteUserId: number) => void;
}) {
  const [chains, setChains] = useState<ChainRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState<number[]>([]); // empty = all
  const [buyingId, setBuyingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notifyApi, notifyHolder] = notification.useNotification();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // taller touch targets below the md breakpoint
  const [scanOpen, setScanOpen] = useState(false);
  const [scanValue, setScanValue] = useState("");
  // Voucher confirm prompt (an unredeemed voucher scanned while a user is open).
  const [voucherPrompt, setVoucherPrompt] = useState<{
    code: string;
    amountCents: number;
  } | null>(null);
  const [voucherBusy, setVoucherBusy] = useState(false);
  // Live voucher matches for the scan dropdown — codes are alphanumeric, so the
  // operator can type any part (e.g. the last digits) and pick the full voucher.
  const [voucherMatches, setVoucherMatches] = useState<
    { code: string; amount_cents: number; status: string }[]
  >([]);
  const treeScrollRef = useRef<HTMLDivElement>(null);
  // Bumped on every new scan + on close, so a slow voucher preview that resolves
  // after the operator moved on is discarded instead of popping a stale prompt.
  const scanSeqRef = useRef(0);

  // Reset the article list to the top whenever the selected user changes, so a
  // new person doesn't inherit where the last scroll was left.
  useEffect(() => {
    if (treeScrollRef.current) treeScrollRef.current.scrollTop = 0;
  }, [strichlisteUserId]);

  // Load ALL chains once (incl. inactive — listArticleChains sends no active
  // filter, so the backend returns every state; ChainRow.active flags which).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const rows = await api.listArticleChains();
        if (!cancelled) setChains(rows);
      } catch (e) {
        if (!cancelled && e instanceof ApiError) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Distinct categories present in the catalogue (drives the filter + tree).
  const categories = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of chains) {
      const id = c.category_id ?? UNCAT;
      if (!map.has(id)) map.set(id, c.category_name ?? "Uncategorised");
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [chains]);

  const buy = async (ch: ChainRow) => {
    if (strichlisteUserId === null) return;
    setBuyingId(ch.chain_root_id);
    setError(null);
    try {
      const res = await api.buyArticleForUser(strichlisteUserId, ch.current_leaf_id);
      onBought?.(res.transaction_id);
      const key = `buy-${res.transaction_id}`;
      notifyApi.open({
        key,
        message: `Bought ${res.article_name ?? ch.name}`,
        description: `${fmtCents(Math.abs(res.amount_cents))} charged to ${
          userName ?? "the user"
        }.`,
        btn: (
          <Button
            size="small"
            onClick={() => void undo(res.transaction_id, key)}
          >
            Undo
          </Button>
        ),
        duration: 6,
      });
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
    } finally {
      setBuyingId(null);
    }
  };

  const undo = async (txId: number, key: string) => {
    if (strichlisteUserId === null) return;
    try {
      await api.undoUserTransaction(strichlisteUserId, txId);
      notifyApi.destroy(key);
      onBought?.();
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
    }
  };

  // Scan-to-buy: resolve the scanned barcode against the loaded chains (their
  // barcodes are already on ChainRow), buy it, then close the scan window.
  // Reuses the stocktake keyboard-wedge scanner while the window is open.
  // One-shot scan window: any TERMINAL outcome closes it; errors surface as a
  // toast. The window only stays open for the voucher "add to user" confirm.
  const closeScan = () => {
    scanSeqRef.current += 1; // supersede any in-flight voucher preview
    setScanOpen(false);
    setScanValue("");
    setVoucherPrompt(null);
  };
  const openScan = () => {
    setScanValue("");
    setVoucherPrompt(null);
    setScanOpen(true);
  };

  const handleScan = (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    scanSeqRef.current += 1; // this scan supersedes any earlier in-flight preview
    // 1) Article barcode — exact first, then a UNIQUE SUBSTRING fallback so a
    // partially-read code (e.g. a damaged barcode) still resolves when exactly
    // one article's barcode CONTAINS it. The substring auto-buy needs >=4 chars
    // so a short string can't silently buy the wrong article; ambiguous matches
    // ask for more digits.
    let ch = chains.find((c) => c.barcodes.some((b) => b.barcode === code));
    if (!ch && code.length >= 4) {
      const matches = chains.filter((c) =>
        c.barcodes.some((b) => b.barcode.includes(code)),
      );
      if (matches.length === 1) {
        ch = matches[0];
      } else if (matches.length > 1) {
        notifyApi.error({
          message: `Several articles match "${code}" — type a few more digits.`,
        });
        return; // keep the window open so the operator can refine
      }
    }
    if (ch) {
      if (strichlisteUserId === null) {
        notifyApi.error({ message: "Select a user first." });
        closeScan();
        return;
      }
      closeScan();
      void buy(ch);
      return;
    }
    // 2) Otherwise treat it as a voucher (mirrors the patched kiosk flow).
    void handleVoucherScan(code);
  };

  // Voucher scan: preview → redeemed jumps to the holder; unredeemed asks to add
  // it to the current user. Mirrors docker/admin-extras/user-scanner.js.
  const handleVoucherScan = async (code: string) => {
    const seq = scanSeqRef.current;
    setVoucherBusy(true);
    try {
      const p = await api.voucherPreviewPublic(code);
      // The operator closed the window or started a newer scan while the preview
      // was in flight — drop this (stale) result rather than popping a prompt.
      if (seq !== scanSeqRef.current) return;
      if (p.status === "redeemed") {
        if (p.redeemed_user_id != null) {
          const uid = p.redeemed_user_id;
          closeScan();
          onSelectUser?.(uid);
        } else {
          notifyApi.error({ message: "Voucher already redeemed." });
          closeScan();
        }
      } else if (p.status === "unredeemed") {
        if (strichlisteUserId === null) {
          notifyApi.error({
            message: "Select a user first to add this voucher.",
          });
          closeScan();
        } else {
          // Open the confirm step (its own modal → works from any scan source).
          setScanOpen(false);
          setVoucherPrompt({ code, amountCents: p.amount_cents ?? 0 });
        }
      } else if (p.status === "disabled") {
        notifyApi.error({ message: "This voucher has been disabled." });
        closeScan();
      } else if (p.status === "not_found") {
        notifyApi.error({ message: `Unknown code: ${code}` });
        closeScan();
      } else {
        // Missing / unexpected status — the preview returned no usable body
        // (server error, or the voucher system is unconfigured).
        notifyApi.error({ message: "Couldn't read the code — server error." });
        closeScan();
      }
    } catch {
      if (seq === scanSeqRef.current) {
        notifyApi.error({ message: "Network error reading the code." });
        closeScan();
      }
    } finally {
      setVoucherBusy(false);
    }
  };

  const confirmAddVoucher = async () => {
    const p = voucherPrompt;
    if (!p || strichlisteUserId === null) return;
    setVoucherBusy(true);
    try {
      const r = await api.voucherRedeemPublic(p.code, strichlisteUserId);
      if (r.status === "redeemed" || r.status === "already_redeemed") {
        const uid = r.redeemed_user_id;
        closeScan();
        if (uid != null) onSelectUser?.(uid);
        else onBought?.();
      } else if (r.status === "disabled") {
        notifyApi.error({ message: "This voucher has been disabled." });
        closeScan();
      } else {
        notifyApi.error({ message: r.error ?? "Redemption failed." });
        closeScan();
      }
    } catch {
      notifyApi.error({ message: "Network error during redemption." });
      closeScan();
    } finally {
      setVoucherBusy(false);
    }
  };
  // Pause the wedge scanner while the voucher confirm is up.
  useBarcodeScanner((code) => handleScan(code), scanOpen && voucherPrompt === null);

  // Picking a live suggestion: an article buys it; the voucher row runs the
  // voucher flow on the typed code.
  const handleScanOption = (val: string) => {
    if (val.startsWith("art:")) {
      const id = Number(val.slice(4));
      const ch = chains.find((c) => c.chain_root_id === id);
      if (!ch) return;
      if (strichlisteUserId === null) {
        notifyApi.error({ message: "Select a user first." });
        closeScan();
        return;
      }
      closeScan();
      void buy(ch);
    } else if (val.startsWith("voucher:")) {
      setScanValue("");
      void handleVoucherScan(val.slice("voucher:".length));
    }
  };

  // Debounced voucher partial lookup — codes are alphanumeric; the backend does
  // a substring match on the code so the operator can type any part (e.g. the
  // last digits) and pick the full voucher from the dropdown.
  useEffect(() => {
    const q = scanValue.trim();
    if (!scanOpen || q.length < 3) {
      setVoucherMatches([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      void api
        .listVouchers({ q })
        .then((r) => {
          if (!cancelled) {
            setVoucherMatches(
              (r.vouchers ?? []).slice(0, 6).map((v) => ({
                code: v.code,
                amount_cents: v.amount_cents,
                status: v.status,
              })),
            );
          }
        })
        .catch(() => {
          if (!cancelled) setVoucherMatches([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [scanValue, scanOpen]);

  // Live suggestions for the manual field: matching articles (barcode CONTAINS
  // the typed text — handles damaged codes) once they narrow to ≤5, then voucher
  // partial matches, with a "redeem exactly this" fallback at the end.
  const scanOptions = useMemo<{ value: string; label: ReactNode }[]>(() => {
    const q = scanValue.trim();
    if (!q) return [];
    const matches = chains.filter((c) =>
      c.barcodes.some((b) => b.barcode.includes(q)),
    );
    const opts: { value: string; label: ReactNode }[] = [];
    if (matches.length > 0 && matches.length <= 5) {
      for (const c of matches) {
        opts.push({
          value: `art:${c.chain_root_id}`,
          label: (
            <span>
              🛒 {c.name} · {fmtCents(c.sell_price_cents)} · {c.expected_qty} in
              stock
            </span>
          ),
        });
      }
    }
    // Voucher partial matches (any part of the alphanumeric code).
    for (const v of voucherMatches) {
      opts.push({
        value: `voucher:${v.code}`,
        label: (
          <span>
            🎟️ {v.code} · {fmtCents(v.amount_cents)} · {v.status}
          </span>
        ),
      });
    }
    // Fallback: redeem exactly what was typed (e.g. the full code, not yet in
    // the match list).
    if (voucherMatches.length === 0) {
      opts.push({
        value: `voucher:${q}`,
        label: <span>🎟️ Redeem voucher “{q}”</span>,
      });
    }
    return opts;
  }, [scanValue, chains, voucherMatches]);

  const treeData: DataNode[] = useMemo(() => {
    const show = catFilter.length === 0 ? null : new Set(catFilter);
    const byCat = new Map<number, ChainRow[]>();
    for (const c of chains) {
      const id = c.category_id ?? UNCAT;
      if (show && !show.has(id)) continue;
      const arr = byCat.get(id);
      if (arr) arr.push(c);
      else byCat.set(id, [c]);
    }
    const UNGROUPED = -1;
    // Global article-row counter so the zebra stripe alternates across ALL
    // article rows (not reset per group); row 0 (the first) is tinted.
    let rowIdx = 0;
    const leaf = (ch: ChainRow): DataNode => {
      const tinted = rowIdx % 2 === 0;
      rowIdx += 1;
      return {
        key: `art:${ch.chain_root_id}`,
        isLeaf: true,
        selectable: false,
        title: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              width: "100%",
              padding: "3px 6px",
              borderRadius: 4,
              background: tinted ? ZEBRA_BG : undefined,
            }}
          >
            <span style={{ opacity: ch.active ? 1 : 0.5 }}>
              {ch.name}
              {!ch.active && <Text type="secondary"> (inactive)</Text>}
              <Text type="secondary"> · {fmtCents(ch.sell_price_cents)}</Text>
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <Text
                type={ch.expected_qty <= 0 ? "danger" : "secondary"}
                style={{ fontSize: 12, whiteSpace: "nowrap" }}
              >
                {ch.expected_qty}
              </Text>
              <Button
                size="small"
                type="primary"
                loading={buyingId === ch.chain_root_id}
                disabled={strichlisteUserId === null || buyingId !== null}
                onClick={() => void buy(ch)}
                style={
                  isMobile
                    ? { height: 40, paddingInline: 18, fontSize: 15 }
                    : undefined
                }
              >
                Buy
              </Button>
            </span>
          </div>
        ),
      };
    };
    return categories
      .filter((cat) => byCat.has(cat.id))
      .map((cat) => {
        const catRows = byCat.get(cat.id) ?? [];
        // Sub-bucket each category by product group (null → "Ungrouped"),
        // restoring the Category → Group → Article hierarchy.
        const byGroup = new Map<number, { name: string; rows: ChainRow[] }>();
        for (const c of catRows) {
          const gid = c.product_group_id ?? UNGROUPED;
          let b = byGroup.get(gid);
          if (!b) {
            b = { name: c.product_group_name ?? "Ungrouped", rows: [] };
            byGroup.set(gid, b);
          }
          b.rows.push(c);
        }
        const groups = [...byGroup.entries()].sort((a, b) => {
          if (a[0] === UNGROUPED) return 1; // Ungrouped bucket last
          if (b[0] === UNGROUPED) return -1;
          return a[1].name.localeCompare(b[1].name);
        });
        return {
          key: `cat:${cat.id}`,
          selectable: false,
          title: (
            <Text strong>
              {cat.name} <Text type="secondary">({catRows.length})</Text>
            </Text>
          ),
          children: groups.map(([gid, g]) => ({
            key: `cat:${cat.id}:grp:${gid}`,
            selectable: false,
            title: (
              <Text type="secondary">
                {g.name} ({g.rows.length})
              </Text>
            ),
            children: g.rows
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(leaf),
          })),
        };
      });
    // buy/undo are stable enough; deps below drive the re-render of buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chains, categories, catFilter, buyingId, strichlisteUserId, userName, isMobile]);

  return (
    <Card
      size="small"
      title="Buy an article"
      bordered={!isMobile}
      style={{ height: "100%" }}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: isMobile ? 0 : undefined,
        },
      }}
    >
      {notifyHolder}
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Select
          mode="multiple"
          size="large"
          allowClear
          loading={loading}
          placeholder="Filter categories (all if empty)…"
          value={catFilter}
          onChange={setCatFilter}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          style={{ flex: 1 }}
        />
        <Button
          size="large"
          icon={<CameraOutlined />}
          disabled={strichlisteUserId === null}
          onClick={openScan}
        >
          Scan
        </Button>
      </div>
      {strichlisteUserId === null ? (
        <Text type="secondary">Select a user to buy for.</Text>
      ) : null}
      <div
        ref={treeScrollRef}
        style={{ flex: 1, overflow: "auto", minHeight: 200 }}
      >
        {treeData.length === 0 ? (
          <Text type="secondary">
            {loading ? "Loading articles…" : "No articles."}
          </Text>
        ) : (
          <Tree
            className="buy-tree"
            blockNode
            selectable={false}
            defaultExpandAll
            treeData={treeData}
          />
        )}
      </div>

      {/* Always-reachable scan shortcut, fixed bottom-right — opens the SAME
          scan window as the inline button (camera preview + manual entry). */}
      <FloatButton
        type="primary"
        icon={<CameraOutlined style={{ fontSize: 26 }} />}
        tooltip="Scan to buy"
        onClick={openScan}
        style={{ width: 60, height: 60 }}
      />

      {/* Scan window: a live camera preview (when supported) AND a manual /
          USB-wedge entry field — both feed the same resolver. */}
      <Modal
        open={scanOpen}
        onCancel={closeScan}
        footer={null}
        title="Scan to buy"
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {cameraScanSupported && (
            <CameraScanView
              active={scanOpen}
              onDetected={(code) => handleScan(code)}
              onError={(m) => notifyApi.error({ message: m })}
            />
          )}
          <Text>
            Scan a product barcode to buy it for{" "}
            <Text strong>{userName ?? "the user"}</Text>, or scan / type a
            voucher. The window closes after the scan.
          </Text>
          <AutoComplete
            value={scanValue}
            onChange={(v) => setScanValue(v)}
            onSelect={(val) => handleScanOption(String(val))}
            options={scanOptions}
            filterOption={false}
            style={{ width: "100%" }}
          >
            <Input
              autoFocus
              allowClear
              inputMode="numeric"
              placeholder="…or type any part of a code + Enter"
              onPressEnter={() => handleScan(scanValue)}
            />
          </AutoComplete>
          {voucherBusy && <Text type="secondary">Checking…</Text>}
        </Space>
      </Modal>

      {/* Voucher "add to current user" confirm — its own modal so it works
          whether the code came from the camera, the wedge, or manual entry. */}
      <Modal
        open={voucherPrompt !== null}
        onCancel={closeScan}
        footer={null}
        title="Voucher"
      >
        {voucherPrompt && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Text>
              Add a <Text strong>{fmtCents(voucherPrompt.amountCents)}</Text>{" "}
              voucher to <Text strong>{userName ?? "this user"}</Text>?
            </Text>
            <Space>
              <Button
                type="primary"
                loading={voucherBusy}
                onClick={() => void confirmAddVoucher()}
              >
                Add voucher
              </Button>
              <Button disabled={voucherBusy} onClick={closeScan}>
                Cancel
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
    </Card>
  );
}
